import React, { useMemo, useState } from 'react';
import {
  Image,
  ImageBackground,
  Platform,
  StyleSheet,
  Text,
  View,
  type ImageSourcePropType,
  type LayoutChangeEvent,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { BACKDROPS, SHARE_MEDALLIONS } from '../../screens/dashboard/user/gamificationAssets';
import { fontWeights } from '../../styles/typographyTokens';
import type {
  CanonicalShareMedallionKey,
  GamificationShareViewModel,
} from './shareCard';

import { accentTextOn } from '../../styles/accentText';
const UI_FONT = Platform.OS === 'web' ? 'var(--ag-font-ui)' : undefined;
const BRAND_FONT = Platform.select({ web: 'var(--ag-font-brand)', ios: 'Georgia', default: 'serif' });

const SHARE_MEDALLION: Record<CanonicalShareMedallionKey, ImageSourcePropType> = {
  ...SHARE_MEDALLIONS,
};

interface Props {
  model: GamificationShareViewModel;
  isDark: boolean;
  testID?: string;
}

/**
 * CORE-709 — the single approved 1200:630 composition used by all six share
 * variants. The preview scales the delivery's 540px geometry proportionally;
 * the web exporter uses the same view model and export tokens at 1200×630.
 */
export const GamificationShareCard: React.FC<Props> = ({ model, isDark, testID }) => {
  const [width, setWidth] = useState(540);
  const scale = Math.max(0.48, Math.min(1, width / 540));
  const px = (value: number) => value * scale;
  const foreground = isDark ? '#F8EEE5' : '#2C251F';
  const muted = isDark ? 'rgba(239,228,218,0.72)' : 'rgba(64,54,46,0.72)';
  const footer = isDark ? 'rgba(239,228,218,0.78)' : 'rgba(62,52,45,0.78)';
  const initial = useMemo(() => model.displayName.trim().charAt(0).toLocaleUpperCase('ru-RU') || 'A', [model.displayName]);

  const onLayout = (event: LayoutChangeEvent) => {
    const next = event.nativeEvent.layout.width;
    if (next > 0 && Math.abs(next - width) > 0.5) setWidth(next);
  };

  return (
    <ImageBackground
      testID={testID}
      source={isDark ? BACKDROPS.share.dark : BACKDROPS.share.light}
      resizeMode="cover"
      imageStyle={[
        styles.backgroundImage,
        Platform.OS === 'web' ? ({ objectPosition: 'center 56%' } as any) : null,
      ]}
      style={[
        styles.card,
        {
          borderColor: isDark ? 'rgba(235,132,52,0.58)' : 'rgba(213,104,27,0.55)',
          borderRadius: px(20),
          ...(Platform.OS === 'web' ? {
            boxShadow: isDark
              ? '0 16px 38px rgba(0,0,0,0.38)'
              : '0 15px 34px rgba(93,52,20,0.16)',
          } as any : null),
        },
      ]}
      onLayout={onLayout}
      accessibilityLabel={`${model.category}. ${model.title}. ${model.value}. ${model.displayName}.`}
      accessibilityIgnoresInvertColors
    >
      <LinearGradient
        pointerEvents="none"
        colors={isDark
          ? ['rgba(9,13,17,0.98)', 'rgba(9,13,17,0.90)', 'rgba(9,13,17,0.42)', 'rgba(9,13,17,0.18)']
          : ['rgba(255,252,246,0.98)', 'rgba(255,249,239,0.88)', 'rgba(255,247,232,0.35)', 'rgba(255,246,228,0.16)']}
        locations={[0, 0.5, 0.8, 1]}
        start={{ x: 0, y: 0.5 }}
        end={{ x: 1, y: 0.5 }}
        style={StyleSheet.absoluteFillObject}
      />
      <View
        pointerEvents="none"
        style={[
          styles.heroGlow,
          Platform.OS === 'web' ? ({
            backgroundImage: isDark
              ? 'radial-gradient(circle at 52% 46%, rgba(237,125,36,0.14) 0%, rgba(237,125,36,0) 69%)'
              : 'radial-gradient(circle at 52% 46%, rgba(255,245,218,0.72) 0%, rgba(255,255,255,0) 69%)',
          } as any) : null,
        ]}
      />
      <View
        pointerEvents="none"
        style={[
          styles.orbit,
          {
            width: px(270),
            height: px(270),
            borderRadius: px(135),
            right: px(-42),
            top: px(-84),
            borderColor: 'rgba(221,111,31,0.18)',
          },
        ]}
      >
        <View style={[styles.orbitInner, { inset: px(34), borderRadius: px(101), borderColor: 'rgba(221,111,31,0.10)' } as any]} />
        <View style={[styles.orbitInner, { inset: px(68), borderRadius: px(67), borderColor: 'rgba(221,111,31,0.07)' } as any]} />
      </View>

      <View style={[styles.inside, { paddingTop: px(22), paddingHorizontal: px(26), paddingBottom: px(20) }]}>
        <View style={styles.brandRow}>
          <Text style={[styles.brand, { color: foreground, fontSize: px(22), lineHeight: px(26) }]}>
            Agap<Text style={styles.brandAccent}>ë</Text>lla
          </Text>
          <Text style={[styles.pathMark, { color: isDark ? 'rgba(247,235,225,0.64)' : 'rgba(59,47,38,0.67)', fontSize: px(10), lineHeight: px(14), letterSpacing: px(1.2) }]}>ПУТЬ СВЕТА</Text>
        </View>

        <View style={[styles.main, { gap: px(23) }]}>
          <View
            style={[
              { width: px(118), height: px(118) },
              Platform.OS === 'web' ? ({ filter: 'drop-shadow(0 7px 10px rgba(0,0,0,0.28))' } as any) : styles.medallionShadow,
            ]}
          >
            {model.icon.kind === 'single' ? (
              <Image
                source={SHARE_MEDALLION[model.icon.key]}
                resizeMode="contain"
                style={{ width: px(118), height: px(118) }}
                accessibilityIgnoresInvertColors
              />
            ) : (
              <View
                style={{ width: px(118), height: px(118) }}
                accessibilityLabel="Пламя Любви, Свет Души и текущий уровень"
              >
                <Image source={SHARE_MEDALLION.flame} resizeMode="contain" style={[styles.triplet, { width: px(69), height: px(69), left: 0, top: px(39) }]} accessibilityIgnoresInvertColors />
                <Image source={SHARE_MEDALLION.soul_light} resizeMode="contain" style={[styles.triplet, { width: px(69), height: px(69), right: 0, top: px(39) }]} accessibilityIgnoresInvertColors />
                <Image source={SHARE_MEDALLION[model.icon.levelKey]} resizeMode="contain" style={[styles.triplet, { width: px(69), height: px(69), left: px(25), top: 0 }]} accessibilityIgnoresInvertColors />
              </View>
            )}
          </View>
          <View style={styles.copy}>
            <Text
              numberOfLines={1}
              style={[styles.category, { color: accentTextOn(isDark ? '#F18A3B' : '#C85B11'), fontSize: px(11), lineHeight: px(15), letterSpacing: px(1.21) }]}
            >
              {model.category.toLocaleUpperCase('ru-RU')}
            </Text>
            <Text numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.72} style={[styles.title, { color: foreground, marginTop: px(3), fontSize: px(32), lineHeight: px(39) }]}>
              {model.title}
            </Text>
            <Text numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.72} style={[styles.value, { color: isDark ? '#A0C864' : '#618F35', marginTop: px(4), fontSize: px(18), lineHeight: px(24) }]}>
              {model.value}
            </Text>
            <Text numberOfLines={1} style={[styles.description, { color: muted, marginTop: px(3), fontSize: px(12), lineHeight: px(17) }]}>
              {model.description}
            </Text>
          </View>
        </View>

        <View style={[styles.footer, { paddingTop: px(11), borderColor: isDark ? 'rgba(234,210,191,0.14)' : 'rgba(128,85,55,0.16)' }]}>
          <View style={[styles.person, { gap: px(8) }]}>
            <View style={[styles.avatar, { width: px(25), height: px(25), borderRadius: px(12.5), borderColor: 'rgba(213,104,27,0.40)', backgroundColor: isDark ? 'rgba(19,25,31,0.72)' : 'rgba(255,252,247,0.70)' }]}>
              <Text style={[styles.avatarText, { color: accentTextOn(isDark ? '#F18A3B' : '#C85B11'), fontSize: px(10) }]}>{initial}</Text>
            </View>
            <Text numberOfLines={1} style={[styles.personName, { color: footer, fontSize: px(11), lineHeight: px(15) }]}>{model.displayName}</Text>
          </View>
          <Text style={[styles.cardUrl, { color: isDark ? 'rgba(239,228,218,0.56)' : 'rgba(67,57,50,0.58)', fontSize: px(10), lineHeight: px(14), letterSpacing: px(0.3) }]}>agapella.io</Text>
        </View>
      </View>
    </ImageBackground>
  );
};

const styles = StyleSheet.create({
  card: {
    width: '100%',
    aspectRatio: 1200 / 630,
    overflow: 'hidden',
    borderWidth: 1,
    backgroundColor: '#F4CDA7',
  },
  backgroundImage: { resizeMode: 'cover' },
  heroGlow: { position: 'absolute', width: '62%', height: '120%', right: '-3%', top: '-40%' },
  orbit: { position: 'absolute', borderWidth: 1 },
  orbitInner: { position: 'absolute', borderWidth: 1 },
  inside: { flex: 1 },
  brandRow: { flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between' },
  brand: { fontFamily: BRAND_FONT, fontWeight: '500', letterSpacing: -0.66 },
  brandAccent: { color: accentTextOn('#E36F1D') },
  pathMark: { fontFamily: UI_FONT, fontWeight: fontWeights.bold },
  main: { flex: 1, flexDirection: 'row', alignItems: 'center' },
  triplet: { position: 'absolute' },
  copy: { flex: 1, minWidth: 0 },
  category: { fontFamily: UI_FONT, fontWeight: fontWeights.bold },
  title: { fontFamily: BRAND_FONT, fontWeight: '500', letterSpacing: -0.64 },
  value: { fontFamily: UI_FONT, fontWeight: fontWeights.bold },
  description: { fontFamily: UI_FONT },
  footer: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', borderTopWidth: 1 },
  person: { minWidth: 0, flex: 1, flexDirection: 'row', alignItems: 'center' },
  avatar: { flexShrink: 0, alignItems: 'center', justifyContent: 'center', borderWidth: 1 },
  avatarText: { fontFamily: UI_FONT, fontWeight: fontWeights.bold },
  personName: { flexShrink: 1, fontFamily: UI_FONT, fontWeight: fontWeights.bold },
  cardUrl: { flexShrink: 0, marginLeft: 8, fontFamily: UI_FONT, fontWeight: fontWeights.bold },
  medallionShadow: {
    shadowColor: '#000000',
    shadowOpacity: 0.28,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 7 },
  },
});

export default GamificationShareCard;
